import { Stack } from "@certn/cascadia";
import {
  CopyToClipboard,
  DialogTrigger,
  GhostIconButton,
  Popover,
  PopoverContent,
  Text,
} from "@certn/cascadia-react";
import { RenderContext } from "@certn/cascadia-react/context/RenderContext";
import { css } from "@emotion/css";
import { ControlProps } from "@jsonforms/core";
import { withJsonFormsControlProps } from "@jsonforms/react";
import isNil from "lodash/isNil";
import { PropsWithChildren, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import Markdown from "react-markdown";

import { CascadiaJsonFormContext } from "../CascadiaJsonFormContext";
import {
  formatData,
  handleMissingAndBlankData,
  mapToDisplayValue,
} from "../utils";

export function TextDisplay(props: ControlProps) {
  const options = props.uischema.options || {};
  const { t } = useTranslation();
  const { isSummaryForPath } = useContext(CascadiaJsonFormContext);
  const isPDF = useContext(RenderContext).isForPdfGeneration;
  const [isCopyHoverTarget, setIsCopyHoverTarget] = useState(false);

  if (!(options.showInSummary ?? true) && isSummaryForPath?.(props.path)) {
    return null;
  }

  const data =
    options.displayValue !== undefined ? options.displayValue : props.data;

  if (!!options.hideWhenEmpty && (isNil(data) || data === "")) {
    return null;
  }

  const formattedData = mapToDisplayValue(
    handleMissingAndBlankData(formatData(data, options.format)),
    options.displayMapping
  ).toString();
  const copyData = options.copyRegexPattern
    ? formattedData.match(options.copyRegexPattern)?.[0] ?? formattedData
    : formattedData;

  const textWrapper = (props: PropsWithChildren) => {
    return <Text className={styles.popoverContent}>{props.children}</Text>;
  };

  const basicTextElement = () => {
    const showCopyButton = options.copy && data;
    if (showCopyButton && options.copyOnHover) {
      return (
        <span
          className={styles.copyOnHover}
          onMouseEnter={() => setIsCopyHoverTarget(true)}
          onMouseLeave={() => setIsCopyHoverTarget(false)}
        >
          <Text style={options.style ?? "global/body-primary"} as={options.as}>
            {formattedData}
          </Text>
          <CopyToClipboard
            text={copyData}
            hideText
            isHiddenUntilHover={!isCopyHoverTarget}
            testID={options.testId ?? "copy-text"}
            style={options.style ?? "global/body-primary"}
          />
        </span>
      );
    }
    return (
      <span
        className={
          options.wrapperStyles
            ? css({ display: "block" }, options.wrapperStyles)
            : undefined
        }
      >
        <Text
          testID={options.testId}
          style={options.style}
          className={css(
            showCopyButton && styles.textWithCopyButton,
            options.additionalStyles
          )}
          as={options.as}
        >
          {formattedData}
        </Text>
        {showCopyButton && (
          <GhostIconButton
            icon="content_copy"
            testID="copy-text-button"
            aria-label={t("Copy")}
            size={options.copyIconSize ?? "small"}
            className={styles.button}
            onPress={() => navigator.clipboard.writeText(copyData)}
          />
        )}
      </span>
    );
  };

  return options.popoverContent ? (
    <Stack orientation="horizontal" gap="space-2">
      {basicTextElement()}
      {!isPDF && (
        <DialogTrigger>
          <GhostIconButton
            icon="info"
            testID="text-display-info-button"
            aria-label={t("Additional information")}
            className={styles.popoverIcon}
          />
          <Popover placement="top">
            <PopoverContent>
              <Markdown
                components={{
                  p: textWrapper,
                }}
              >
                {options.popoverContent}
              </Markdown>
            </PopoverContent>
          </Popover>
        </DialogTrigger>
      )}
    </Stack>
  ) : (
    basicTextElement()
  );
}

const styles = {
  popoverIcon: css({
    width: "1.1rem",
    height: "1.1rem",
    "& i": {
      color: "var(--icon-weak)",
    },
  }),
  popoverContent: css({ color: "var(--text-weak)" }),
  button: css({ marginLeft: "var(--space-2)", display: "inline" }),
  textWithCopyButton: css({ verticalAlign: "top" }),
  copyOnHover: css({ display: "inline-flex" }),
};

export const TextDisplayRenderer = withJsonFormsControlProps(TextDisplay);
